import React, { useCallback, useContext, useMemo, useState, memo, useEffect, useRef } from 'react';
import { toPng } from 'html-to-image';
import { ArrowForward, Save, Close, AutoAwesome } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import type { ChatAction, AIFormState } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { ChatLoggerContext } from '@trinity/chat-components/containers/Chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useFormFileUpload } from '@trinity/chat-components/hooks/useFormFileUpload';
import { capitalize, requestMicrophonePermissionIfNeeded } from '@trinity/chat-components/utils';
import { CustomElementRenderer } from '@trinity/custom-pages-registry';
import type { apiSchemas } from '@trinity/management-app-client';
import { ActiveChatSessionContext, SubmitActionContext } from '../Chat/context';
import { useSpeechRecognition } from '../Chat/hooks/useSpeechRecognition';
import { CustomPageContextContainer } from '../CustomPageContext';
import { DynamicForm } from './DynamicForm.form';
import { DynamicFormIntroduction } from './DynamicForm.introduction';
import { DynamicFormProbing } from './DynamicForm.probing';
import { expandRepeatableSchema, type FormFieldValue } from './DynamicForm.repeatable.utils';
import { ActionButtonContainer, ActionButton } from './DynamicForm.styles.index';
import type { FormWorkflowIntroConfig } from './iconnect/types';
import { useIconnectArtifactDownloader } from './iconnect/useIconnectArtifactDownloader';

const CONSTANTS = {
  INSIGHT_MIN_LENGTH: 10,
  INSIGHT_MEANINGFUL_LENGTH: 20,
  ERROR_MESSAGES: {
    REQUIRED_FIELDS: 'Please fill in all required fields.',
    INSIGHT_TOO_SHORT: 'Please provide a more detailed insight (minimum 10 characters).',
    SUBMIT_FAILED: 'Failed to save interaction. Please try again.',
    GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
  },
  MESSAGES: {
    SAVING: 'Saving...',
    SAVE: 'Save',
    SUBMIT: 'Proceed',
    SUBMITTING: 'Proceeding...',
    SAVE_AS_DRAFT: 'Save as Draft',
    CANCEL: 'Cancel',
    SUBMITTED: 'Submitted',
  },
} as const;

type FormSchema = apiSchemas.FormSchema;

type Step = 'initial' | 'probing' | 'manual-entry' | 'draft' | 'submitted' | 'cancelled';

const getTodaysDate = (): string => {
  return new Date().toISOString().split('T')[0] ?? '';
};

const hasSubmissionMetadata = (propsValue: Props['value'], savedSubmittedAt?: string): boolean => {
  return Boolean(savedSubmittedAt || propsValue.submittedAt);
};

const computeStepFromProps = (
  propsValue: Props['value'],
  currentStep: Step,
  isCancelled: boolean,
  isEditingDraft: boolean,
  savedSubmittedAt?: string
): { step: Step; clearEditing: boolean } | null => {
  const newFormState = propsValue.formState ?? 'initial';
  const status = propsValue.status;

  const shouldShowDraftList =
    propsValue.draftOnlyMode === true &&
    propsValue.availableDrafts &&
    Array.isArray(propsValue.availableDrafts) &&
    propsValue.availableDrafts.length > 0;

  if (
    status === 'form-completion' ||
    newFormState === 'submitted' ||
    hasSubmissionMetadata(propsValue, savedSubmittedAt)
  ) {
    return { step: 'submitted', clearEditing: true };
  }
  if (shouldShowDraftList) {
    return { step: 'initial', clearEditing: true };
  }
  if (currentStep !== 'submitted' && currentStep !== 'cancelled' && !isCancelled) {
    if (!isEditingDraft && newFormState && newFormState !== currentStep && newFormState !== 'initial') {
      return { step: newFormState, clearEditing: false };
    }
  }
  return null;
};

const buildInitialFormData = (
  propsValue: Props['value'],
  formConfig: FormSchema | undefined
): Record<string, unknown> => {
  const data: Record<string, unknown> = {};

  if (formConfig?.fields) {
    formConfig.fields.forEach((field) => {
      let value = propsValue[field.name];
      if (value === undefined && propsValue[field.label]) {
        value = propsValue[field.label];
      }

      const hasExplicitValue =
        value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '');

      const hasDefaultValue =
        field.defaultValue !== undefined &&
        field.defaultValue !== null &&
        !(typeof field.defaultValue === 'string' && field.defaultValue.trim() === '');

      const optionDefaultValue =
        field.type === 'radio' || field.type === 'select'
          ? field.options?.find((opt) => opt.defaultSelected)?.value
          : undefined;

      let resolvedValue: unknown;
      if (hasExplicitValue) {
        resolvedValue = value;
      } else if (hasDefaultValue) {
        resolvedValue = field.defaultValue;
      } else {
        resolvedValue = optionDefaultValue;
      }

      data[field.name] = resolvedValue;
    });
  }

  if (!data.date) {
    data.date = getTodaysDate();
  }

  const existingInsight = (propsValue.insight as string) || '';
  const shouldUseExistingInsight =
    existingInsight &&
    existingInsight.length > CONSTANTS.INSIGHT_MEANINGFUL_LENGTH &&
    !existingInsight.toLowerCase().includes('log an interaction');

  if (shouldUseExistingInsight) {
    data.insight = existingInsight;
  }

  return data;
};

const buildFilteredFormData = (formConfig: FormSchema, dynamicFormData: Record<string, unknown>) => {
  return formConfig.fields
    .map((field) => ({
      // Use stable field name keys so draft formData is consistent across save paths.
      fieldLabel: field.name,
      fieldType: field.type,
      value: dynamicFormData[field.name] ?? '',
    }))
    .filter((item) => {
      if (item.fieldType === 'static') {
        return true;
      }
      if (item.fieldType === 'variable') {
        return true;
      }
      return item.value !== null && item.value !== undefined && item.value !== '';
    });
};

const validateDynamicFormData = (data: Record<string, unknown>, config: FormSchema | undefined): boolean => {
  if (!config || !config.fields) {
    return true;
  }
  const requiredFields = config.fields.filter((field) => field.required);
  return requiredFields.every((field) => {
    const value = data[field.name];
    if (!value) {
      return false;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    if (field.name === 'insight' && typeof value === 'string') {
      return value.trim().length >= CONSTANTS.INSIGHT_MIN_LENGTH;
    }
    return true;
  });
};

type Props = {
  queryId: number;
  queryIdentifier?: string;
  value: Record<string, unknown> & {
    formState?: Step;
    draftId?: string;
    showButtons?: boolean;
    isFromSpeech?: boolean;
    formConfig?: FormSchema;
    aiState?: AIFormState;
    submittedData?: {
      formData?: Array<{
        fieldLabel: string;
        fieldType: string;
        value: unknown;
      }>;
    };
    submittedAt?: string | number | Date;
    draftAt?: string | number | Date;
    availableDrafts?: Array<{
      id: string;
      lastUpdated: string;
      formName?: string;
      previewText: string;
    }>;
    draftOnlyMode?: boolean; // When true, only show draft options, hide new entry buttons
    sendToEndpoint?: boolean; // when true, button shows "Proceed" with an arrow icon instead of "Save"
  };
  workflowType?: string;
  actionType?: string;
  introConfig?: FormWorkflowIntroConfig;
  submitBehavior?: {
    onBeforeSubmit?: (dynamicFormData: Record<string, unknown>) => Promise<void>;
    onRequestSubmit?: (controls: {
      submitWithPreSubmit: () => Promise<{ draftId?: string; submittedAt?: string } | void>;
      submitOnly: () => Promise<{ draftId?: string; submittedAt?: string } | void>;
      dynamicFormData: Record<string, unknown>;
      isValid: boolean;
      isSubmitting: boolean;
    }) => Promise<void> | void;
  };
  externalSubmitError?: string | null;
  onDashboardCapture?: (dataUrl: string | undefined) => void;
};

const sanitizeDashboardImageDataUrl = (dataUrl: string | undefined): string | undefined => {
  if (!dataUrl) {
    return undefined;
  }

  const trimmed = dataUrl.trim();
  if (!trimmed || trimmed === 'data:,' || trimmed === 'data:') {
    return undefined;
  }

  if (!trimmed.startsWith('data:image/')) {
    return undefined;
  }

  return trimmed;
};

type RenderWizardStepParams = {
  currentStep: Step;
  formConfig: FormSchema;
  dynamicFormData: Record<string, unknown>;
  formDataToDisplay: Record<string, unknown>;
  isValid: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  showButtons: boolean;
  aiState: AIFormState | undefined;
  correctionsApplied: boolean;
  listening: boolean;
  isListeningInInitial: boolean;
  isListeningInProbing: boolean;
  props: Props;
  handleStartManualEntry: () => void;
  handleStartSpeechEntry: () => void;
  handleCancel: () => Promise<void>;
  handleLoadDraft: (draftId: string) => Promise<void>;
  handleFieldChange: (fieldName: string, value: unknown) => void;
  handleFieldsChange: (updates: Record<string, FormFieldValue>) => void;
  handleAICorrection: (fieldName: string, originalValue: unknown, correctedValue: unknown) => void;
  handleProbingManualEntry: () => Promise<void>;
  handleProbingSpeechEntry: () => Promise<void>;
  onSubmit: () => Promise<void>;
  onDraft: () => Promise<void>;
  onEdit: () => void;
  onDownloadPdf?: () => Promise<void>;
  isDownloadingPdf?: boolean;
  onEnhance?: () => Promise<void>;
  isEnhancing?: boolean;
  handleFileUploadProceed: (file: File) => Promise<void>;
  isFileExtracting: boolean;
  submittedAtOverride?: string;
};

const renderWizardStep = (params: RenderWizardStepParams): React.ReactElement | null => {
  const { currentStep, formConfig, props, showButtons: _showButtons } = params;

  switch (currentStep) {
    case 'initial':
      return renderInitialStep(params);
    case 'probing':
      return renderProbingStep(params);
    case 'manual-entry':
      return renderManualEntryStep(params);
    case 'draft':
      return renderDraftStep(params);
    case 'submitted':
      return renderSubmittedStep(params);
    case 'cancelled':
      return (
        <DynamicFormIntroduction
          onManualEntry={params.handleStartManualEntry}
          onSpeechEntry={params.handleStartSpeechEntry}
          onCancel={params.handleCancel}
          prompt={
            props.introConfig?.prompt || formConfig?.prompt?.initialMessage || 'Please fill out the form'
          }
          warningMessage={props.introConfig?.warningMessage}
          showCancelButton={false}
          showSpeechOption={false}
          showManualButton={false}
        />
      );
    // no default
  }
};

const resolveIntroVisibility = (
  isListeningInInitial: boolean,
  introConfig: Props['introConfig'],
  showButtons: boolean
) => ({
  showCancelButton: isListeningInInitial ? false : (introConfig?.showCancelButton ?? showButtons),
  showSpeechOption: isListeningInInitial ? false : (introConfig?.showSpeechOption ?? showButtons),
  showManualButton: isListeningInInitial ? false : (introConfig?.showManualButton ?? showButtons),
  // Opt-in only: unset means no file upload button
  showFileUploadOption: isListeningInInitial ? false : (introConfig?.showFileUploadOption ?? false),
});

const renderInitialStep = (params: RenderWizardStepParams): React.ReactElement => {
  const { formConfig, props, showButtons, isListeningInInitial, listening } = params;
  const visibility = resolveIntroVisibility(isListeningInInitial, props.introConfig, showButtons);
  return (
    <DynamicFormIntroduction
      onManualEntry={params.handleStartManualEntry}
      onSpeechEntry={params.handleStartSpeechEntry}
      onCancel={params.handleCancel}
      onLoadDraft={params.handleLoadDraft}
      prompt={props.introConfig?.prompt || formConfig?.prompt?.initialMessage || 'Please fill out the form'}
      warningMessage={props.introConfig?.warningMessage}
      showCancelButton={visibility.showCancelButton}
      showSpeechOption={visibility.showSpeechOption}
      showManualButton={visibility.showManualButton}
      showFileUploadOption={visibility.showFileUploadOption}
      isVoiceActive={isListeningInInitial || listening}
      onFileUploadProceed={params.handleFileUploadProceed}
      isFileExtracting={params.isFileExtracting}
      availableDrafts={props.value.availableDrafts}
      draftOnlyMode={props.value.draftOnlyMode}
      introResponse={props.introConfig?.introResponse}
      dashboardElement={props.introConfig?.dashboardElement}
    />
  );
};

const resolveProbingVisibility = (
  isListeningInProbing: boolean,
  introConfig: Props['introConfig'],
  showButtons: boolean,
  isSubmitting: boolean
) => ({
  showCancelButton: !isListeningInProbing && showButtons && !isSubmitting,
  showSpeechOption: !isListeningInProbing && (introConfig?.showSpeechOption ?? showButtons) && !isSubmitting,
  showManualButton: !isListeningInProbing && (introConfig?.showManualButton ?? showButtons) && !isSubmitting,
});

const renderProbingStep = (params: RenderWizardStepParams): React.ReactElement => {
  const { props, showButtons, isSubmitting, aiState, isListeningInProbing, listening } = params;
  const visibility = resolveProbingVisibility(
    isListeningInProbing,
    props.introConfig,
    showButtons,
    isSubmitting
  );
  return (
    <DynamicFormProbing
      onManualEntry={params.handleProbingManualEntry}
      onSpeechEntry={params.handleProbingSpeechEntry}
      onCancel={params.handleCancel}
      missingRequiredFields={aiState?.missingRequiredFields || []}
      retryCount={aiState?.retryCount ?? 0}
      maxRetries={aiState?.maxRetries ?? 3}
      showCancelButton={visibility.showCancelButton}
      showSpeechOption={visibility.showSpeechOption}
      showManualButton={visibility.showManualButton}
      isVoiceActive={isListeningInProbing || listening}
    />
  );
};

const renderManualEntryStep = (params: RenderWizardStepParams): React.ReactElement => {
  const {
    formConfig,
    dynamicFormData,
    isValid,
    isSubmitting,
    submitError,
    aiState,
    correctionsApplied,
    props,
    onEnhance,
    isEnhancing,
  } = params;
  const sendToEndpoint = params.props.value.sendToEndpoint ?? false;
  const submitLabels = sendToEndpoint
    ? { idle: CONSTANTS.MESSAGES.SUBMIT, busy: CONSTANTS.MESSAGES.SUBMITTING }
    : { idle: CONSTANTS.MESSAGES.SAVE, busy: CONSTANTS.MESSAGES.SAVING };
  const submitLabel = isSubmitting ? submitLabels.busy : submitLabels.idle;
  const submitIcon = sendToEndpoint ? ArrowForward : Save;
  return (
    <>
      <DynamicForm
        formConfig={formConfig}
        formData={dynamicFormData}
        onFieldChange={params.handleFieldChange}
        onFieldsChange={params.handleFieldsChange}
        isValid={isValid}
        isSubmitting={isSubmitting}
        isSubmitted={false}
        submitError={submitError}
        hasPrefilledData={Object.keys(props.value).length > 1}
        aiState={aiState}
        onAICorrection={params.handleAICorrection}
        correctionsApplied={correctionsApplied}
        showButtons={false}
        onDraft={params.onDraft}
      />
      <ActionButtonContainer>
        {onEnhance && (
          <ActionButton
            onClick={onEnhance}
            variant="primary"
            disabled={isEnhancing || isSubmitting}
            type="button"
            icon={AutoAwesome}
          >
            {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
          </ActionButton>
        )}
        <ActionButton
          onClick={params.onSubmit}
          variant="primary"
          disabled={isSubmitting || !isValid}
          icon={submitIcon}
        >
          {submitLabel}
        </ActionButton>
        <ActionButton
          onClick={params.onDraft}
          variant="primary"
          disabled={isSubmitting}
          type="button"
          icon={Save}
        >
          {isSubmitting ? CONSTANTS.MESSAGES.SAVING : CONSTANTS.MESSAGES.SAVE_AS_DRAFT}
        </ActionButton>
        <ActionButton variant="secondary" onClick={params.handleCancel} disabled={isSubmitting} icon={Close}>
          {CONSTANTS.MESSAGES.CANCEL}
        </ActionButton>
      </ActionButtonContainer>
    </>
  );
};

const renderDraftStep = (params: RenderWizardStepParams): React.ReactElement => {
  const {
    formConfig,
    formDataToDisplay,
    isValid,
    isSubmitting,
    submitError,
    aiState,
    correctionsApplied,
    props,
    onEnhance,
    isEnhancing,
  } = params;
  return (
    <DynamicForm
      formConfig={formConfig}
      formData={formDataToDisplay}
      onFieldChange={params.handleFieldChange}
      onFieldsChange={params.handleFieldsChange}
      isValid={isValid}
      isSubmitting={isSubmitting}
      isDraft={true}
      submitError={submitError}
      hasPrefilledData={Object.keys(props.value).length > 1}
      aiState={aiState}
      onAICorrection={params.handleAICorrection}
      correctionsApplied={correctionsApplied}
      showButtons={true}
      onEdit={params.onEdit}
      draftAt={props.value.draftAt}
      onEnhance={onEnhance}
      isEnhancing={isEnhancing}
    />
  );
};

const renderSubmittedStep = (params: RenderWizardStepParams): React.ReactElement => {
  const {
    formConfig,
    formDataToDisplay,
    isValid,
    isSubmitting,
    submitError,
    aiState,
    correctionsApplied,
    showButtons,
    props,
    onDownloadPdf,
    isDownloadingPdf,
  } = params;
  return (
    <DynamicForm
      formConfig={formConfig}
      formData={formDataToDisplay}
      onFieldChange={params.handleFieldChange}
      onFieldsChange={params.handleFieldsChange}
      isValid={isValid}
      isSubmitting={isSubmitting}
      isSubmitted={true}
      submitError={submitError}
      hasPrefilledData={Object.keys(props.value).length > 1}
      aiState={aiState}
      onAICorrection={params.handleAICorrection}
      correctionsApplied={correctionsApplied}
      showButtons={showButtons}
      submittedAt={props.value.submittedAt ?? params.submittedAtOverride}
      onDownloadPdf={onDownloadPdf}
      isDownloadingPdf={isDownloadingPdf}
    />
  );
};

const DynamicFormWizardContent = memo(({ onDashboardCapture, ...props }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const loggerCtx = useContext(ChatLoggerContext);
  const apiClient = useChatApiClient();
  const downloadArtifact = useIconnectArtifactDownloader();
  const { upload } = useFormFileUpload();
  const workflowType = props.workflowType ?? 'generic-workflow';
  const sendToEndpoint = props.value.sendToEndpoint ?? false;

  const formConfig = useMemo(() => props.value.formConfig, [props.value.formConfig]);

  const valueWithState = useMemo(
    () => ({
      ...props.value,
      formState: props.value.formState ?? 'initial',
      showButtons: props.value.showButtons ?? true,
    }),
    [props.value]
  );

  const initialSubmittedAt =
    typeof props.value.submittedAt === 'string' ? props.value.submittedAt : undefined;

  const resolvedInitialStep =
    computeStepFromProps(valueWithState, valueWithState.formState, false, false, initialSubmittedAt)?.step ??
    valueWithState.formState;

  const [currentStep, setCurrentStep] = useState<Step>(resolvedInitialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [aiState, setAiState] = useState<AIFormState | undefined>(undefined);
  const [correctionsApplied, setCorrectionsApplied] = useState(false);
  const [isListeningInInitial, setIsListeningInInitial] = useState(false);
  const [isListeningInProbing, setIsListeningInProbing] = useState(false);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isFileExtracting, setIsFileExtracting] = useState(false);
  const [capturedDashboardImage, setCapturedDashboardImage] = useState<string | undefined>();
  const [isDashboardDataReady, setIsDashboardDataReady] = useState(false);
  const [savedDraftId, setSavedDraftId] = useState<string | undefined>(props.value.draftId);
  const [savedSubmittedAt, setSavedSubmittedAt] = useState<string | undefined>(initialSubmittedAt);
  const dashboardCaptureRef = useRef<HTMLDivElement>(null);

  const captureDashboardImage = useCallback(() => {
    const el = dashboardCaptureRef.current;
    if (!el) {
      console.error('Dashboard capture element not found');
      return;
    }

    toPng(el, { cacheBust: true })
      .then((dataUrl) => {
        const sanitizedDataUrl = sanitizeDashboardImageDataUrl(dataUrl);
        setCapturedDashboardImage(sanitizedDataUrl);
        onDashboardCapture?.(sanitizedDataUrl);
      })
      .catch(() => undefined);
  }, [onDashboardCapture]);

  useEffect(() => {
    if (props.introConfig?.dashboardElement == null) return;

    const el = dashboardCaptureRef.current;
    if (!el) return;

    const fallbackTimer = setTimeout(() => {
      captureDashboardImage();
    }, 5000);

    if (!isDashboardDataReady) {
      console.warn('Dashboard data not ready, skipping capture');
      return () => clearTimeout(fallbackTimer);
    }

    const readyTimer = setTimeout(() => {
      captureDashboardImage();
      clearTimeout(fallbackTimer);
    }, 150);

    return () => {
      clearTimeout(fallbackTimer);
      clearTimeout(readyTimer);
    };
  }, [captureDashboardImage, isDashboardDataReady, props.introConfig?.dashboardElement, currentStep]);

  const shouldRenderDashboardAtTop =
    props.introConfig?.dashboardElement != null &&
    ['initial', 'cancelled', 'probing', 'manual-entry'].includes(currentStep);

  useEffect(() => {
    const result = computeStepFromProps(
      props.value,
      currentStep,
      isCancelled,
      isEditingDraft,
      savedSubmittedAt
    );
    if (result) {
      setCurrentStep(result.step);
      if (result.clearEditing) {
        setIsEditingDraft(false);
      }
    }
  }, [
    props.value.formState,
    props.value.status,
    props.value.draftOnlyMode,
    props.value.availableDrafts,
    currentStep,
    props.value.submittedData,
    isSubmitting,
    isCancelled,
    isEditingDraft,
    savedSubmittedAt,
    props.value,
  ]);

  // Reset isSubmitting when backend response changes the form state
  useEffect(() => {
    if (isSubmitting && props.value.formState && props.value.formState !== 'probing') {
      setIsSubmitting(false);
    }
  }, [props.value.formState, isSubmitting, currentStep]);

  useEffect(() => {
    if (props.value.aiState) {
      setAiState(props.value.aiState);
      setCorrectionsApplied(false);
    } else {
      setAiState(undefined);
      setCorrectionsApplied(false);
    }
  }, [props.value.aiState]);

  const expandedFormConfig = useMemo(
    () => (formConfig ? expandRepeatableSchema(formConfig) : undefined),
    [formConfig]
  );

  const initialFormData = useMemo(() => {
    return buildInitialFormData(props.value, expandedFormConfig);
  }, [props.value, expandedFormConfig]);
  const getFormDataWithOriginalValues = useCallback(() => {
    if (aiState?.originalExtraction && aiState?.patternInsights?.some((p) => p.hasCorrectionHistory)) {
      return { ...initialFormData, ...aiState.originalExtraction };
    }
    return initialFormData;
  }, [initialFormData, aiState]);

  const [dynamicFormData, setDynamicFormData] = useState<Record<string, unknown>>(() => {
    return getFormDataWithOriginalValues();
  });

  useEffect(() => {
    if (currentStep === 'initial') {
      let dataToUse = initialFormData;
      if (aiState?.originalExtraction && aiState?.patternInsights?.some((p) => p.hasCorrectionHistory)) {
        dataToUse = { ...initialFormData, ...aiState.originalExtraction };
      }
      setDynamicFormData(dataToUse);
    }
  }, [initialFormData, currentStep, aiState]);
  const submittedFormData = useMemo(() => {
    if (currentStep === 'submitted' && props.value.submittedData?.formData && expandedFormConfig?.fields) {
      const data: Record<string, unknown> = {};
      const submittedData = props.value.submittedData.formData;

      submittedData.forEach((item) => {
        const field = expandedFormConfig.fields.find(
          (f) => f.name === item.fieldLabel || f.label === item.fieldLabel
        );
        if (field) {
          data[field.name] = item.value;
        }
      });

      return data;
    }
    return null;
  }, [currentStep, props.value.submittedData, expandedFormConfig]);

  const formDataToDisplay = submittedFormData || dynamicFormData;
  const isValid = useMemo(() => {
    return validateDynamicFormData(dynamicFormData, expandedFormConfig);
  }, [dynamicFormData, expandedFormConfig]);

  // Speech recognition handlers - updates insight field directly
  const onSpeechFinished = useCallback(
    (value: string, prefix: string) => {
      // Handle undefined or empty prefix to prevent "undefined" appearing in transcript
      const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
      let newVal = safePrefix ? `${safePrefix} ${capitalize(value)}` : capitalize(value);

      // Add punctuation if missing
      if (!endsWithPunctuation(newVal)) {
        newVal = newVal + '.';
      }

      setDynamicFormData((prev) => ({
        ...prev,
        insight: newVal,
      }));

      // After speech finishes in initial step, transition to manual-entry
      if (currentStep === 'initial' && isListeningInInitial) {
        setIsListeningInInitial(false);
      }

      // After speech finishes in probing step, just stop listening
      // User will manually submit via chat input like in initial step
      if (currentStep === 'probing' && isListeningInProbing) {
        setIsListeningInProbing(false);
      }
    },
    [currentStep, isListeningInInitial, isListeningInProbing]
  );

  const onInterimResult = useCallback((result: string, prefix: string) => {
    // Handle undefined or empty prefix to prevent "undefined" appearing in transcript
    const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
    const value = safePrefix ? `${safePrefix} ${capitalize(result)}` : capitalize(result);

    setDynamicFormData((prev) => ({
      ...prev,
      insight: value,
    }));
  }, []);

  const onAudioLevelChange = useCallback((_level: number) => {
    // Audio level monitoring for future use
  }, []);

  const { start, stop, listening } = useSpeechRecognition({
    continuous: true,
    onResult: onSpeechFinished,
    onInterimResult,
    onAudioLevelChange,
  });

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      if (listening) {
        stop().catch((error) => {
          loggerCtx.captureException(error);
        });
      }
    };
  }, [listening, loggerCtx, stop]);

  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    setDynamicFormData((prev) => {
      return { ...prev, [fieldName]: value };
    });
  }, []);
  const handleFieldsChange = useCallback((updates: Record<string, FormFieldValue>) => {
    setDynamicFormData((prev) => ({ ...prev, ...updates }));
  }, []);
  const handleAICorrection = useCallback(
    (fieldName: string, _originalValue: unknown, correctedValue: unknown) => {
      setDynamicFormData((prev) => ({
        ...prev,
        [fieldName]: correctedValue,
      }));

      setCorrectionsApplied(true);
    },
    []
  );
  const performSubmit = useCallback(
    async (runBeforeSubmit: boolean): Promise<{ draftId?: string; submittedAt?: string } | void> => {
      if (!isValid || !submitAction || !formConfig || !expandedFormConfig) {
        return;
      }
      setIsSubmitting(true);
      setSubmitError(null);
      setIsEditingDraft(false);
      const previousStep = currentStep;
      setCurrentStep('submitted');
      try {
        if (runBeforeSubmit && props.submitBehavior?.onBeforeSubmit) {
          await props.submitBehavior.onBeforeSubmit(dynamicFormData);
        }
        const filteredFormData = buildFilteredFormData(expandedFormConfig, dynamicFormData);

        // sendToEndpoint workflows run the agent call inside the chat action so the reply gets the standard loading bubble.
        const saveResult = sendToEndpoint
          ? { draftId: undefined, submittedAt: new Date().toISOString(), message: undefined }
          : await apiClient.chat.saveFormWorkflowSubmission({
              queryId: props.queryId,
              workflowType,
              formParentId: formConfig.parentId,
              formVersion: formConfig.version,
              formData: filteredFormData,
              aiState,
            });

        if (saveResult.draftId) {
          setSavedDraftId(saveResult.draftId);
        }
        if (saveResult.submittedAt) {
          setSavedSubmittedAt(saveResult.submittedAt);
        }

        const action: ChatAction = {
          type: ChatActionType.SubmitFormWorkflow,
          payload: {
            queryId: props.queryId,
            workflowType,
            formParentId: formConfig.parentId,
            formVersion: formConfig.version,
            formState: 'submitted',
            formData: filteredFormData,
            aiState: aiState,
            draftId: saveResult.draftId,
            submittedAt: saveResult.submittedAt,
            message: saveResult.message,
          },
        };
        await submitAction(action, 'Submit');
        return { draftId: saveResult.draftId, submittedAt: saveResult.submittedAt };
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : CONSTANTS.ERROR_MESSAGES.SUBMIT_FAILED);
        setCurrentStep(previousStep);
        return;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      apiClient,
      aiState,
      currentStep,
      dynamicFormData,
      formConfig,
      expandedFormConfig,
      isValid,
      props.queryId,
      props.submitBehavior,
      sendToEndpoint,
      submitAction,
      workflowType,
    ]
  );

  const onSubmit = useCallback(async () => {
    if (props.submitBehavior?.onRequestSubmit) {
      await props.submitBehavior.onRequestSubmit({
        submitWithPreSubmit: () => performSubmit(true),
        submitOnly: () => performSubmit(false),
        dynamicFormData,
        isValid,
        isSubmitting,
      });
      return;
    }

    await performSubmit(true);
  }, [dynamicFormData, isSubmitting, isValid, performSubmit, props.submitBehavior]);

  const onDraft = useCallback(async () => {
    if (!submitAction || !formConfig || !expandedFormConfig) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setIsEditingDraft(false);
    const previousStep = currentStep;
    setCurrentStep('draft');
    try {
      const filteredFormData = buildFilteredFormData(expandedFormConfig, dynamicFormData);
      const action: ChatAction = {
        type: ChatActionType.SubmitFormWorkflow,
        payload: {
          queryId: props.queryId,
          workflowType,
          formParentId: formConfig.parentId,
          formVersion: formConfig.version,
          formState: 'draft',
          formData: filteredFormData,
          aiState: aiState,
        },
      };
      await submitAction(action, 'Save Draft');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : CONSTANTS.ERROR_MESSAGES.SUBMIT_FAILED);
      setCurrentStep(previousStep);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    submitAction,
    dynamicFormData,
    props.queryId,
    formConfig,
    expandedFormConfig,
    aiState,
    currentStep,
    workflowType,
  ]);

  const onEdit = useCallback(() => {
    setIsEditingDraft(true);
    setCurrentStep('manual-entry');
  }, []);

  const handleStartManualEntry = useCallback(() => {
    setCurrentStep('manual-entry');
  }, []);

  // Voice entry toggle - matches InputControl.Container pattern exactly
  const handleStartSpeechEntry = useCallback(() => {
    if (listening) {
      // If already listening, stop
      stop().catch((error) => {
        loggerCtx.captureException(error);
      });
      setIsListeningInInitial(false);
    } else {
      // Request permission and start listening
      requestMicrophonePermissionIfNeeded()
        .then((granted) => {
          if (granted) {
            // Stay in initial mode but hide buttons and show listening state
            setIsListeningInInitial(true);

            // Ensure we don't pass undefined as prefix
            const currentInsight = dynamicFormData.insight || '';
            const safeInput = currentInsight && typeof currentInsight === 'string' ? currentInsight : '';
            start(safeInput);
          }
        })
        .catch((error) => {
          loggerCtx.captureException(error);
        });
    }
  }, [dynamicFormData.insight, listening, loggerCtx, start, stop]);

  // Probing step handlers
  const handleProbingManualEntry = useCallback(async () => {
    // Transition to manual-entry by submitting to backend
    // This will trigger the proper response message and form state
    if (!submitAction) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit form state update to transition from probing to manual-entry
      const action: ChatAction = {
        type: ChatActionType.SubmitFormWorkflow,
        payload: {
          queryId: props.queryId,
          workflowType,
          formState: 'manual-entry',
          formData: [],
          aiState,
        },
      };
      await submitAction(action, 'Continue to form');
      // Backend will respond with manual-entry state and the "I've analyzed your text" message
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to load form');
      setIsSubmitting(false);
    }
  }, [submitAction, props.queryId, aiState, workflowType]);

  const handleProbingSpeechEntry = useCallback(async () => {
    if (listening) {
      stop().catch((error) => {
        loggerCtx.captureException(error);
      });
      setIsListeningInProbing(false);
    } else {
      const granted = await requestMicrophonePermissionIfNeeded();
      if (granted) {
        setIsListeningInProbing(true);
        const currentInsight = dynamicFormData.insight || '';
        const safeInput = currentInsight && typeof currentInsight === 'string' ? currentInsight : '';
        start(safeInput);
      }
    }
  }, [dynamicFormData.insight, listening, loggerCtx, start, stop]);

  const _handleProbingRetry = useCallback(
    async (newTranscript: string) => {
      if (!submitAction || !aiState) {
        return;
      }

      try {
        setIsSubmitting(true);

        // Combine the original transcript with the new one
        const combinedTranscript = aiState.combinedTranscript
          ? `${aiState.combinedTranscript} ${newTranscript}`
          : newTranscript;

        // Don't increment retry count here - backend will handle it
        // Update aiState with combined transcript only
        const updatedAiState: AIFormState = {
          ...aiState,
          combinedTranscript,
        };

        setAiState(updatedAiState);

        // Update the form data with combined transcript
        setDynamicFormData((prev) => ({
          ...prev,
          insight: combinedTranscript,
        }));

        // Submit to backend for re-extraction
        // Backend will create a user message with the new transcript portion
        const action: ChatAction = {
          type: ChatActionType.SubmitFormWorkflow,
          payload: {
            queryId: props.queryId,
            workflowType,
            formState: 'probing',
            formData: [
              {
                fieldLabel: 'insight',
                value: combinedTranscript,
              },
            ],
            aiState: updatedAiState,
          },
        };

        await submitAction(action, 'Retry extraction');

        // Backend will respond with either 'probing' (more missing fields) or 'manual-entry' (all filled)
        // The response will update the state automatically via props
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : 'Failed to process voice input');
      } finally {
        setIsSubmitting(false);
      }
    },
    [submitAction, aiState, props.queryId, workflowType]
  );

  const handleCancel = useCallback(async () => {
    try {
      setIsCancelled(true);
      setSubmitError(null);
      setIsSubmitting(false);
      setDynamicFormData(initialFormData);
      setCurrentStep('cancelled');

      if (submitAction) {
        const action: ChatAction = {
          type: ChatActionType.SubmitFormWorkflow,
          payload: {
            queryId: props.queryId,
            workflowType: workflowType,
            formState: 'cancelled',
            formData: [],
          },
        };
        await submitAction(action, 'Cancel');
      }
    } catch {
      setIsCancelled(true);
      setCurrentStep('cancelled');
    }
  }, [submitAction, props.queryId, initialFormData, workflowType]);

  const handleDownloadPdf = useCallback(async (): Promise<void> => {
    const draftId = props.value.draftId ?? savedDraftId;
    if (!draftId || isDownloadingPdf) {
      console.error('Draft ID is missing or PDF download is already in progress');
      return;
    }

    setIsDownloadingPdf(true);
    setSubmitError(null);

    try {
      await downloadArtifact({
        draftId,
        dashboardImageBase64: capturedDashboardImage,
      });
    } catch {
      setSubmitError('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [downloadArtifact, props.value.draftId, savedDraftId, isDownloadingPdf, capturedDashboardImage]);

  const enhanceMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      if (!formConfig?.enhanceWithAi) return null;
      const formDataEntries = Object.entries(formData).filter(([, v]) => v !== undefined) as Array<
        [string, string | number | boolean | string[] | null]
      >;
      return apiClient.formEnhance.enhance({
        formSchemaIdentifier: formConfig.id,
        formData: Object.fromEntries(formDataEntries),
      });
    },
    onMutate: () => {
      setSubmitError(null);
    },
    onSuccess: (result) => {
      if (!result) return;
      setDynamicFormData((prev) => {
        const updated = { ...prev };
        for (const { fieldName, value } of result.fields) {
          updated[fieldName] = value;
        }
        return updated;
      });
    },
    onError: () => {
      setSubmitError('Enhancement failed. Please try again.');
    },
  });

  const { mutate: mutateEnhance } = enhanceMutation;
  const handleEnhance = useCallback(async (): Promise<void> => {
    if (!formConfig?.enhanceWithAi) return;
    mutateEnhance(dynamicFormData);
  }, [formConfig?.enhanceWithAi, dynamicFormData, mutateEnhance]);

  const handleFileUploadProceed = useCallback(
    async (file: File) => {
      if (!submitAction) return;
      setIsFileExtracting(true);
      setSubmitError(null);
      try {
        const identifier = await upload(file, null);
        if (!identifier) {
          setSubmitError(CONSTANTS.ERROR_MESSAGES.GENERIC_ERROR);
          return;
        }

        await submitAction(
          {
            type: ChatActionType.ExtractFormFromFile,
            payload: {
              queryId: props.queryId,
              workflowType: workflowType,
              fileUploadIdentifier: identifier,
            },
          },
          'Extract from file'
        );
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : CONSTANTS.ERROR_MESSAGES.GENERIC_ERROR);
      } finally {
        setIsFileExtracting(false);
      }
    },
    [upload, submitAction, props.queryId, workflowType]
  );

  const handleLoadDraft = useCallback(
    async (draftId: string) => {
      if (!submitAction) {
        console.error('Submit action not available');
        return;
      }

      if (isSubmitting) {
        // Prevent multiple simultaneous loads
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const action: ChatAction = {
          type: ChatActionType.LoadDraft,
          payload: {
            queryId: props.queryId,
            draftId: draftId,
          },
        };
        await submitAction(action, 'Load Draft');
      } catch (error) {
        console.error('Error loading draft:', error);
        setSubmitError(error instanceof Error ? error.message : 'Failed to load draft');
        // Don't set isSubmitting to false on error so user can see the error state
      } finally {
        // Only reset submitting state after a short delay to prevent rapid clicks
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      }
    },
    [submitAction, props.queryId, isSubmitting]
  );

  const showButtons = valueWithState.showButtons && !isCancelled;

  if (!formConfig || !expandedFormConfig) {
    return null;
  }

  return (
    <>
      {shouldRenderDashboardAtTop && (
        <div style={{ marginBottom: '16px' }}>
          <div ref={dashboardCaptureRef} style={{ width: '100%', maxWidth: '480px' }}>
            <CustomPageContextContainer onDataReadyChange={setIsDashboardDataReady}>
              <CustomElementRenderer element={props.introConfig.dashboardElement} />
            </CustomPageContextContainer>
          </div>
        </div>
      )}
      {renderWizardStep({
        currentStep,
        formConfig: expandedFormConfig,
        dynamicFormData,
        formDataToDisplay,
        isValid,
        isSubmitting,
        submitError,
        showButtons,
        aiState: aiState ?? undefined,
        correctionsApplied,
        listening,
        isListeningInInitial,
        isListeningInProbing,
        props,
        handleStartManualEntry,
        handleStartSpeechEntry,
        handleCancel,
        handleLoadDraft,
        handleFieldChange,
        handleFieldsChange,
        handleAICorrection,
        handleProbingManualEntry,
        handleProbingSpeechEntry,
        onSubmit,
        onDraft,
        onEdit,
        onDownloadPdf: currentStep === 'submitted' && !sendToEndpoint ? handleDownloadPdf : undefined,
        isDownloadingPdf,
        onEnhance:
          formConfig?.enhanceWithAi && formConfig?.enhanceWithAiPromptIdentifier ? handleEnhance : undefined,
        isEnhancing: enhanceMutation.isPending,
        handleFileUploadProceed,
        isFileExtracting,
        submittedAtOverride: savedSubmittedAt,
      })}
    </>
  );
});

export const DynamicFormWizard = (props: Props) => {
  const chatSession = useContext(ActiveChatSessionContext);

  if (!chatSession) {
    return null;
  }

  return <DynamicFormWizardContent {...props} />;
};

// Helper function to check if string ends with punctuation
function endsWithPunctuation(str: string): boolean {
  const punctuation = /[.,!?;:'"-]/;
  return punctuation.test(str.charAt(str.length - 1));
}
